"use client";

import { useEffect, useRef, useState } from "react";
import { Activity, ChartBar as BarChart3, Clock, Database, FileCode, Sparkles, Webhook } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Capability = {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  visualType: "sql" | "chart" | "chips" | "thumbs" | "cron" | "endpoint";
};
const capabilities: Capability[] = [{
  id: "design",
  icon: FileCode,
  title: "Custom SEO Strategy\xA0 \xA0",
  description: "Every strategy starts with your services, target customers, locations, competitors, and current website performance.",
  visualType: "sql"
}, {
  id: "seo",
  icon: BarChart3,
  title: "Strategic SEO",
  description: "We create SEO strategies that help your website rank for the searches your customers actually use. Our focus is not just traffic — it is visibility that turns into calls, inquiries, bookings, and sales.",
  visualType: "chart"
}, {
  id: "local",
  icon: Database,
  title: "Local SEO & Google Fixes",
  description: "Resolve Google Business Profile suspensions and dominate local search results. We ensure your business is found by local customers.",
  visualType: "chips"
}, {
  id: "analytics",
  icon: Activity,
  title: "Performance Tracking",
  description: "Monitor your growth with clear, actionable insights. We track what matters so you can focus on scaling your business.",
  visualType: "thumbs"
}, {
  id: "automation",
  icon: Clock,
  title: "Automated Reporting",
  description: "Stay informed with scheduled performance reports delivered directly to your inbox. Transparency at every step.",
  visualType: "cron"
}];
function LivePulseChart() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(id);
  }, []);
  const points = [12, 18, 14, 22, 19, 26, 24, 30];
  const shifted = [...points.slice(tick % 3), ...points.slice(0, tick % 3)];
  const max = 32;
  const w = 200;
  const h = 48;
  const step = w / (shifted.length - 1);
  const path = shifted.map((v, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - v / max * h}`).join(" ");
  return <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" preserveAspectRatio="none">
      <path d={path} fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={h - shifted[shifted.length - 1] / max * h} r="3" fill="var(--primary)">
        <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>;
}
function TileVisual({
  type
}: {
  type: Capability["visualType"];
}) {
  if (type === "sql") {
    return <div className="font-mono text-xs leading-relaxed rounded-lg border p-3 mt-2" style={{
      backgroundColor: "var(--background)",
      borderColor: "var(--border)"
    }}>
        <div>
          <span style={{
          color: "var(--primary)"
        }}>Rank</span>{" "}
          <span style={{
          color: "var(--muted-foreground)"
        }} className="!text-[rgba(60,167,221,1)]"><span className="!text-[rgba(103,111,126,1)]">higher</span></span>
        </div>
        <div>
          {" "}
          <span style={{
          color: "var(--foreground)"
        }} className="!text-[rgba(23,28,38,0.64)]"><span className="!text-[rgba(23,28,38,0.51)]"></span></span>{" "}
          <span style={{
          color: "var(--primary)"
        }}>OPTIMIZE</span>{" "}
          <span style={{
          color: "var(--muted-foreground)"
        }} className="!text-[rgba(103,111,126,1)]">
            conversion rate
          </span>
        </div>
      </div>;
  }
  if (type === "chart") {
    return <div className="mt-2">
        <LivePulseChart />
      </div>;
  }
  if (type === "chips") {
    const chips = ["Google", "Maps", "Local"];
    return <div className="flex gap-2 mt-2 flex-wrap">
        {chips.map((c, i) => <span key={c} data-index={i} className="font-mono text-xs px-2.5 py-1 rounded-lg border" style={{
        backgroundColor: "var(--background)",
        borderColor: "var(--border)",
        color: "var(--accent-foreground)"
      }}>
            {c}
          </span>)}
      </div>;
  }
  if (type === "thumbs") {
    return <div className="grid grid-cols-3 gap-2 mt-2">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="h-12 rounded-lg border flex items-end justify-around p-1.5" style={{
        backgroundColor: "var(--background)",
        borderColor: "var(--border)"
      }}>
            {i === 0 && [40, 70, 55, 85].map((v, j) => <div key={j} className="w-1.5 rounded-sm" style={{
          height: `${v}%`,
          backgroundColor: j === 3 ? "var(--primary)" : "var(--muted-foreground)"
        }} />)}
            {i === 1 && <svg viewBox="0 0 40 24" className="w-full h-full">
                <polyline points="0,18 10,12 20,14 30,6 40,8" fill="none" stroke="var(--primary)" strokeWidth="1.5" />
              </svg>}
            {i === 2 && <svg viewBox="0 0 24 24" className="w-8 h-8">
                <circle cx="12" cy="12" r="9" fill="none" stroke="var(--muted-foreground)" strokeWidth="3" strokeDasharray="40 60" />
                <circle cx="12" cy="12" r="9" fill="none" stroke="var(--primary)" strokeWidth="3" strokeDasharray="20 80" strokeDashoffset="-40" />
              </svg>}
          </div>)}
      </div>;
  }
  if (type === "cron") {
    return <div className="font-mono text-xs px-3 py-2 rounded-lg border mt-2 flex items-center gap-2" style={{
      backgroundColor: "var(--background)",
      borderColor: "var(--border)"
    }}>
        <Clock className="size-3.5" style={{
        color: "var(--primary)"
      }} />
        <span style={{
        color: "var(--muted-foreground)"
      }}>
          {"Weekly Report"}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wider" style={{
        color: "var(--primary)"
      }}>
          Every Monday
        </span>
      </div>;
  }
  if (type === "endpoint") {
    return <div className="font-mono text-xs px-3 py-2 rounded-lg border mt-2 flex items-center gap-2" style={{
      backgroundColor: "var(--background)",
      borderColor: "var(--border)"
    }}>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{
        backgroundColor: "var(--accent)",
        color: "var(--primary)"
      }}>
          SYNC
        </span>
        <span style={{
        color: "var(--muted-foreground)"
      }}>
          /marketing/stack
        </span>
      </div>;
  }
  return null;
}
function CapabilityTile({
  capability,
  index
}: {
  capability: Capability;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = capability.icon;
  return <div ref={ref} data-index={index} className="group rounded-2xl border p-7 transition-all duration-300 ease-out hover:-translate-y-1" style={{
    backgroundColor: "var(--card)",
    borderColor: "var(--border)",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionProperty: "opacity, transform, border-color, box-shadow",
    transitionDuration: "600ms, 600ms, 280ms, 280ms",
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${index * 100}ms`
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = "var(--primary)";
    e.currentTarget.style.boxShadow = "0 0 0 1px var(--primary), 0 12px 40px -12px hsl(200 70% 55% / 0.2)";
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = "var(--border)";
    e.currentTarget.style.boxShadow = "none";
  }}>
      <div className="flex items-start gap-4">
        <div className="flex size-10 items-center justify-center border rounded-lg shrink-0 transition-colors duration-300" style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--background)"
      }}>
          <Icon className="size-5" style={{
          color: "var(--primary)"
        }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-default font-semibold leading-tight tracking-tight !text-xl" style={{
          color: "var(--foreground)"
        }}>
            {capability.title}
          </h3>
          <p className="mt-2 leading-relaxed !text-lg" style={{
          color: "var(--muted-foreground)"
        }}>
            {capability.description}
          </p>
          <TileVisual type={capability.visualType} />
        </div>
      </div>
    </div>;
}
export default function WhyChooseAsahi() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const stickyRef = useRef<HTMLDivElement>(null);
  return <section data-section-id="12268" id="whychooseasahi" className="bg-background text-foreground ![padding:40px_32px_100px_32px]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {}
          <div className="!opacity-[0.96] lg:col-span-7">
            <div ref={stickyRef} className="!w-[500px] lg:sticky lg:top-32">
              <div ref={headerReveal.ref} style={{
              opacity: headerReveal.isVisible ? 1 : 0,
              transform: headerReveal.isVisible ? "translateY(0)" : "translateY(24px)",
              transition: "opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)"
            }} className="!w-[600px]">
                <div className="inline-flex items-center gap-2 mb-6">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium uppercase tracking-[0.12em]" style={{
                  backgroundColor: "var(--accent)",
                  color: "var(--primary)"
                }}>
                    <Sparkles className="size-3" />
                    Why Asahi Search
                  </span>
                </div>

                <h2 className="font-default font-semibold leading-[1.05] tracking-tight" style={{
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                color: "var(--foreground)"
              }}><span className="!h-[120px]" contenteditable="false" style={{}}><span className="!h-[200px]">Organic Growth, <span className="italic font-medium" contenteditable="false">Simplified</span>.</span></span></h2>

                <p className="mt-6 max-w-xl leading-relaxed !text-lg" style={{
                fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
                color: "var(--muted-foreground)"
              }}>Asahi Search empowers small and medium businesses to thrive online. We optimize your site structure and content to ensure you rank higher and attract qualified leads organically and consistently.</p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild variant="outline" className="text-sm inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border shadow-xs py-2 has-[>svg]:px-3 rounded-lg h-12 px-6 border-[var(--border)] bg-transparent !text-lg disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-[var(--card)] hover:text-foreground text-primary">
                    
                  </Button>
                  <Button asChild variant="outline" className="text-sm inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border shadow-xs py-2 has-[>svg]:px-3 rounded-lg h-12 px-6 border-[var(--border)] bg-transparent !text-lg disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-[var(--card)] hover:text-foreground text-primary">
                    <Link href="https://www.asahisearch.com/contact-us/" newTab>Get a Free SEO Audit</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5">
            <div className="flex flex-col gap-4">
              {capabilities.map((cap, i) => <CapabilityTile key={cap.id} capability={cap} index={i} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}